import { api } from '../../api';
import type { StudioForm } from './studio-api';
import { record, rows, text } from './studio-content';

/** Блок «Съёмка» (APP-SPEC-5 §1.9): общие правила для листа и для ячеек карточки «Ещё настройки».
    Словари — только из GET /app/settings/dicts, строки и умолчания экрана — из settings.shoot формы (03-api §6.6). */
export const shootLabels: Record<string, string> = {
  camera: 'Камера',
  lens: 'Объектив',
  aperture: 'Диафрагма',
  light: 'Свет',
  color: 'Цвет',
  angle: 'Ракурс',
  move: 'Движение',
  tempo: 'Темп',
};
export type ShootItem = Record<string, unknown>;
export interface ShootPolicy {
  rows: string[];
  defaults: Record<string, string>;
  hideTags: string[];
  hideIds: string[];
  styleWord: string;
}
const strings = (value: unknown) => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []);

/** settings.shoot формы: в проде — `settings.shoot`, в ранних ответах — `shoot` на верхнем уровне формы. */
export function shootPolicy(form: StudioForm | null | undefined): ShootPolicy | null {
  const source = record(form);
  const raw = record(source.shoot ?? record(source.settings).shoot);
  const ownRows = strings(raw.rows).filter(kind => Object.hasOwn(shootLabels, kind));
  if (!ownRows.length) return null;
  const defaults: Record<string, string> = {};
  for (const [kind, id] of Object.entries(record(raw.defaults))) if (typeof id === 'string' && id) defaults[kind] = id;
  return { rows: ownRows, defaults, hideTags: strings(raw.hide_tags), hideIds: strings(raw.hide_ids), styleWord: text(raw.style_word) };
}

export function shootMedia(form: StudioForm | null | undefined): 'photo' | 'video' {
  return form?.screen === 'video' ? 'video' : 'photo';
}

/** Строки блока, которые видны сейчас: что в рамке и пресет видео сужают набор (§1.9 «Где блок и какие строки»). */
export function shootKinds(form: StudioForm | null | undefined, inputs: Record<string, string>): string[] {
  const policy = shootPolicy(form);
  if (!policy) return [];
  if (shootMedia(form) === 'photo') return policy.rows;
  const mode = record(form?.constraints?.video).mode;
  if (mode === 'parallax') return [];
  return policy.rows.filter(kind =>
    inputs.video
      ? ['camera', 'light', 'color'].includes(kind)
      : mode === 'product'
        ? ['camera', 'lens', 'aperture', 'light'].includes(kind)
        : mode === 'text' || kind !== 'angle',
  );
}

export function shootSelection(raw: string | undefined): Record<string, string> {
  try {
    const value = JSON.parse(raw ?? '{}') as unknown;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(Object.entries(value).filter(([, id]) => typeof id === 'string')) as Record<string, string>;
  } catch {
    return {};
  }
}

let dictionaries: Promise<Record<string, unknown>> | null = null;
/** Словари кэшируются на всё время приложения (ETag и max-age=60 на сервере): лист и карточка читают один ответ. */
export function loadShootDicts(signal?: AbortSignal): Promise<Record<string, unknown>> {
  if (!dictionaries)
    dictionaries = api.request<Record<string, unknown>>('/app/settings/dicts').catch(failure => {
      dictionaries = null;
      throw failure;
    });
  const shared = dictionaries;
  if (!signal) return shared;
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException('aborted', 'AbortError'));
    if (signal.aborted) return abort();
    signal.addEventListener('abort', abort, { once: true });
    shared.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}
/** Только для тестов: сбросить кэш словарей. */
export function resetShootDicts() {
  dictionaries = null;
}

export function shootItems(catalog: unknown): ShootItem[] {
  const source = record(catalog);
  return rows(record(source.shoot ?? source).items);
}

export interface ShootContext {
  items: ShootItem[];
  selected: Record<string, string>;
  policy: ShootPolicy;
  media: 'photo' | 'video';
  style: boolean;
  reference: boolean;
  drawn: boolean;
}
/** Контекст экрана поверх формы: у фотосессий умолчание задаёт образ или сцена набора, а не поле style. */
export interface ShootOverrides {
  /** Выбран стиль, образ или сцена — умолчание «Света» и «Цвета» «Как в стиле» */
  styled?: boolean;
  /** Выбран свой референс — умолчание «Как на картинке» */
  referenced?: boolean;
  /** Рисованный стиль: камера, объектив и диафрагма гаснут */
  drawn?: boolean;
  /** Подпись служебного пункта «Как в стиле»: «Как в образе», «Как в сцене» */
  styleWord?: string;
  /** Скрыть пункты с этими метками или id (товарные экраны: lofi, border, stamp, distort…) */
  hideTags?: string[];
  hideIds?: string[];
}
export function shootContext(
  form: StudioForm | null | undefined,
  inputs: Record<string, string>,
  catalog: unknown,
  overrides: ShootOverrides = {},
): ShootContext | null {
  const base = shootPolicy(form);
  if (!base) return null;
  const policy: ShootPolicy = {
    ...base,
    styleWord: overrides.styleWord ?? base.styleWord,
    hideTags: [...new Set([...base.hideTags, ...(overrides.hideTags ?? [])])],
    hideIds: [...new Set([...base.hideIds, ...(overrides.hideIds ?? [])])],
  };
  const media = shootMedia(form);
  const styles = rows(record(form?.constraints?.[media]).styles);
  const reference = overrides.referenced ?? !!inputs.reference;
  return {
    items: shootItems(catalog),
    selected: shootSelection(inputs.shoot),
    policy,
    media,
    style: (overrides.styled ?? (!!inputs.style && inputs.style !== 'none')) && !reference,
    reference,
    drawn: overrides.drawn ?? styles.some(style => String(style.id) === inputs.style && style.look === 'drawn'),
  };
}
/** Пункт словаря подходит экрану: вид, носитель, скрытые метки и id экрана. */
export function shootVisible(context: ShootContext, item: ShootItem, kind: string) {
  return (
    item.kind === kind &&
    ['both', context.media].includes(text(item.media) || 'both') &&
    !context.policy.hideIds.includes(text(item.id)) &&
    !strings(item.tags).some(tag => context.policy.hideTags.includes(tag))
  );
}
export function shootItem(context: ShootContext, kind: string, id: string | undefined) {
  return id ? context.items.find(item => item.kind === kind && item.id === id) : undefined;
}
/** Умолчание строки в текущем контексте: явное умолчание экрана, при референсе — «Как на картинке», при стиле — «Как в стиле», иначе «Авто». */
export function shootDefault(context: ShootContext, kind: string): string {
  const own = context.policy.defaults[kind];
  const service = (requires: string) => context.items.find(item => shootVisible(context, item, kind) && item.requires === requires);
  if (context.reference && service('reference')) return text(service('reference')!.id);
  if (own) return own;
  if (context.style && service('style')) return text(service('style')!.id);
  return 'auto';
}
export function shootCamera(context: ShootContext) {
  return shootItem(context, 'camera', context.selected.camera ?? 'auto');
}
export function shootLens(context: ShootContext) {
  const camera = shootCamera(context);
  return camera?.fixed_lens === true ? camera : shootItem(context, 'lens', context.selected.lens ?? 'auto');
}
/** Причина, по которой строка неактивна (§1.9 «Правила»), или пусто. */
export function shootRowOff(context: ShootContext, kind: string): string {
  const camera = shootCamera(context),
    lens = shootLens(context);
  if (context.drawn && ['camera', 'lens', 'aperture'].includes(kind)) return 'Рисованному стилю камера не нужна';
  if (kind === 'lens' && camera?.fixed_lens === true) return 'У этой камеры объектив встроенный';
  if (kind === 'aperture' && camera?.fixed_aperture === true) return 'У этой камеры резкость не настраивается';
  if (kind === 'aperture' && lens?.fixed_aperture === true) return 'У этого объектива диафрагма постоянная';
  return '';
}
/** Подпись служебного пункта по экрану («Как в образе», «Как в сцене»). */
export function shootItemName(context: ShootContext, item: ShootItem | undefined, screen?: string): string {
  if (!item) return 'Авто';
  const byScreen = record(item.label_by_screen);
  if (screen && typeof byScreen[screen] === 'string') return String(byScreen[screen]);
  if (item.requires === 'style' && context.policy.styleWord) return context.policy.styleWord;
  return text(item.name) || 'Авто';
}
/** Значение строки для листа и ячейки. */
export function shootRowValue(context: ShootContext, kind: string, screen?: string): string {
  const off = shootRowOff(context, kind);
  if (kind === 'lens' && off === 'У этой камеры объектив встроенный') return 'Встроенный';
  if (kind === 'aperture' && off && off !== 'Рисованному стилю камера не нужна') return 'Как у камеры';
  const id = context.selected[kind] ?? shootDefault(context, kind);
  return shootItemName(context, shootItem(context, kind, id), screen);
}
/** Строка изменена человеком: выбор есть, отличается от умолчания контекста и строка не погашена. */
export function shootRowChanged(context: ShootContext, kind: string): boolean {
  const id = context.selected[kind];
  return !!id && id !== shootDefault(context, kind) && !shootRowOff(context, kind);
}
